import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isMenuSection, type MenuDefinition } from '@/shared/layouts/menu/types';

/**
 * RAH topbar — TB-01..TB-09 plus the cross-role rules from
 * `_共通ルール-シェル.md`:
 *
 * - R-01 breadcrumb hidden (`.top-breadcrumb`, kept in the DOM for a11y)
 * - R-03 current page is icon + text, no chrome
 * - R-04 two metric zones: `.header-tiles.fn` (per screen) | `.header-tiles.sys`
 *        (施術予定 / 施術実施 / 返戻, fixed across the whole system)
 * - R-06 height locked at 60px; overflow scrolls the metric strip, never grows
 *
 * Metric values are not wired to an API yet, so tiles render the
 * ConceptDesign's own unknown-value marker (`—`) rather than invented numbers.
 */

export interface RahTile {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** `undefined` renders `—`, the ConceptDesign marker for "no value yet". */
  value?: number | string;
  unit?: string;
  /** CSS colour token, e.g. `var(--danger)`. */
  tone?: string;
  onClick?: () => void;
  title?: string;
  /**
   * Zone this tile belongs to. Tiles sharing a group render inside one
   * `.header-tiles.fn`; a change of group opens a new one behind a `.tiles-sep`
   * (R-04 — 保険証 and 同意書 are separate readings, not one strip of six).
   * Ungrouped tiles all land in a single box, which is the old behaviour.
   */
  group?: string;
}

export interface RahTopbarProps {
  menu: MenuDefinition;
  tenantPrefix: string;
  /** Left zone: metrics for the screen in view. */
  functionTiles?: RahTile[];
  /** Right zone: system-wide metrics. Defaults to the fixed R-04 trio. */
  systemTiles?: RahTile[];
  /** Service switch (A2 / R2) rendered as `.svc-tabs`. */
  serviceTabs?: React.ReactNode;
  /** Clinic / scope filter, pinned right by `.clinic-filter-wrap { order: 99 }`. */
  scopeFilter?: React.ReactNode;
  /** Utility buttons (お知らせ, 通知, タスク, ヘルプ...). */
  actions?: React.ReactNode;
}

/** R-04: the right zone is identical on every screen. */
export const DEFAULT_SYSTEM_TILES: RahTile[] = [
  { key: 'planned', label: '施術予定', icon: '📅', unit: '件', tone: 'var(--info)' },
  { key: 'done', label: '施術実施', icon: '✅', unit: '件', tone: 'var(--success)' },
  { key: 'returned', label: '返戻', icon: '↩', unit: '件', tone: 'var(--danger)' },
];

const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

/**
 * Consecutive tiles sharing a `group` form one box. Consecutive, not sorted by
 * key: the caller's order is the reading order on screen, and reordering it
 * here would move tiles the screen deliberately placed.
 */
function groupTiles(tiles: RahTile[]): RahTile[][] {
  return tiles.reduce<RahTile[][]>((boxes, tile) => {
    const last = boxes[boxes.length - 1];
    if (last && last[0].group === tile.group) last.push(tile);
    else boxes.push([tile]);
    return boxes;
  }, []);
}

const Tile: React.FC<{ tile: RahTile }> = ({ tile }) => (
  <div
    className="htile"
    title={tile.title ?? tile.label}
    role={tile.onClick ? 'button' : undefined}
    tabIndex={tile.onClick ? 0 : undefined}
    onClick={tile.onClick}
    onKeyDown={e => {
      if (tile.onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        tile.onClick();
      }
    }}
  >
    <div className="htile-ico" style={{ color: tile.tone }}>
      {tile.icon}
    </div>
    <div className="htile-body">
      <div className="htile-label">{tile.label}</div>
      <div className="htile-stat">
        <span className="htile-num" style={tile.value === undefined ? undefined : { color: tile.tone }}>
          {tile.value ?? '—'}
        </span>
        {tile.unit && <span className="htile-unit">{tile.unit}</span>}
      </div>
    </div>
  </div>
);

export const RahTopbar: React.FC<RahTopbarProps> = ({
  menu,
  tenantPrefix,
  functionTiles = [],
  systemTiles = DEFAULT_SYSTEM_TILES,
  serviceTabs,
  scopeFilter,
  actions,
}) => {
  const currentPath = normalize(useLocation().pathname);

  /** Resolve the menu entry that owns this URL — it supplies icon + title. */
  const current = useMemo(() => {
    const resolve = (p: string) => normalize(p === '/' ? tenantPrefix || '/' : `${tenantPrefix}${p}`);

    for (const tab of menu.tabs) {
      for (const group of tab.groups) {
        if (group.path && resolve(group.path) === currentPath) {
          return { icon: group.icon, label: group.label, category: null as string | null };
        }
        for (const entry of group.items ?? []) {
          if (!isMenuSection(entry) && resolve(entry.path) === currentPath) {
            return { icon: entry.icon, label: entry.label, category: group.label };
          }
        }
      }
    }
    return null;
  }, [menu, currentPath, tenantPrefix]);

  return (
    <header className="topbar">
      {serviceTabs && <div className="svc-tabs">{serviceTabs}</div>}

      {/* R-01: kept in the DOM, hidden by CSS. */}
      <div className="top-breadcrumb">
        {current?.category && (
          <>
            {current.category}
            <span className="sep">›</span>
          </>
        )}
      </div>

      {/* R-03: icon + text only. */}
      <div className="current-page-btn">
        {current ? (
          <>
            <span className="cpb-ico">{current.icon}</span>
            {current.label}
          </>
        ) : null}
      </div>

      <div className="top-tools">
        {groupTiles(functionTiles).map((box, index) => (
          <React.Fragment key={box[0].group ?? box[0].key}>
            {index > 0 && <div className="tiles-sep" />}
            <div className="header-tiles fn">
              {box.map(tile => (
                <Tile key={tile.key} tile={tile} />
              ))}
            </div>
          </React.Fragment>
        ))}

        {functionTiles.length > 0 && systemTiles.length > 0 && <div className="tiles-sep" />}

        {systemTiles.length > 0 && (
          <div className="header-tiles sys">
            {systemTiles.map(tile => (
              <Tile key={tile.key} tile={tile} />
            ))}
          </div>
        )}

        {actions && (
          <>
            <div className="tb-divider" />
            {actions}
          </>
        )}

        {scopeFilter && <div className="clinic-filter-wrap">{scopeFilter}</div>}
      </div>
    </header>
  );
};

export default RahTopbar;
